/**
 * main.tsx — app entry point.
 *
 * Provider stack (outermost to innermost):
 *   StrictMode
 *     QueryClientProvider — Tanstack Query
 *       ThemeProvider     — Tensaw design tokens (light, comfortable)
 *         AuthProvider    — JWT mirror of sessionStorage["portal_jwt"]
 *           AppRouter     — react-router with all routes
 *
 * `bootstrap()` is fired before render. It's a void-call (not awaited)
 * because the route-level RequireAuth + page-level query suspensions give
 * MSW enough time to start before the first real fetch fires.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider } from '@tensaw/design-system/theme';
import '@tensaw/design-system/styles/global.css';

import { AppRouter } from './AppRouter';
import { AuthProvider } from './auth/AuthContext';
import { bootstrap } from './bootstrap';

// Kick off bootstrap (MSW in mock mode) before render. Idempotent.
void bootstrap();

// Local QueryClient — the patient portal is a small enough app that a
// shared one from `@tensaw/runtime` would force a transitive dep on
// staff-app concerns. Defaults are tuned for the portal: short stale time,
// no retry on auth errors (the 401 handler in api/client.ts already
// redirects, so retries would loop).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error: unknown) => {
        const status = (error as { problem?: { status?: number } })?.problem?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider mode="light" density="comfortable">
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
